import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import DonateButton from "@/components/DonateButton";
import { IMG, BOARD } from "@/lib/site";

export default function About() {
  return (
    <>
      <section className="relative overflow-hidden bg-charcoal pb-20 pt-40 text-ivory lg:pb-28 lg:pt-52" data-testid="about-hero">
        <img src="https://images.unsplash.com/photo-1761998940222-a9c2d2d8c7ab?auto=format&fit=crop&w=1200&q=75" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" fetchpriority="high" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 via-charcoal/60 to-charcoal" />
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6 text-gold">About Nseya Foundation</p>
            <h1 className="max-w-4xl text-balance font-display text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl" data-testid="about-hero-headline">
              The road always <span className="italic text-gold">leads back home.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="bg-ivory py-24 lg:py-32" data-testid="founder-story-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-4">
            <div className="sticky top-28">
              <Reveal>
                <img src="/brand/founder-portrait.jpg" alt="Nseya Ariane Kamba, founder of the Nseya Foundation" className="aspect-[3/4] w-full rounded-2xl object-cover" loading="lazy" data-testid="founder-portrait-image" />
                <div className="mt-6 rounded-2xl bg-forest p-7 text-ivory" data-testid="founder-name-card">
                  <p className="font-display text-2xl font-semibold">Nseya Ariane Kamba</p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-gold">Founder</p>
                  <p className="mt-4 text-sm leading-relaxed text-ivory/75">
                    "Nseya" — pronounced <em>say-yah</em> — is a name carried in memory of an aunt who passed tragically in Congo, a woman she never met.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <div className="space-y-6 text-lg leading-relaxed text-mutedink" data-testid="founder-story-body">
                <p className="text-2xl leading-snug text-forest sm:text-3xl">I was born in Africa to mixed African parents and raised in Canada.</p>
                <p>
                  Growing up with a father who carried his Congolese pride loudly — music playing, fufu and pondu on the table, wearing traditional clothing without apology — Congo was always part of my life. What once embarrassed me became part of the foundation of who I am.
                </p>
                <p>
                  Canada gave me a lifetime of experiences, careers, reinventions and lessons. I have worked across different worlds — including behind the scenes with Live Nation clients and artists, <a href="https://www.elitetherapeutics.ca" target="_blank" rel="noopener noreferrer" className="font-semibold text-forest underline decoration-gold underline-offset-4 hover:text-earth" data-testid="elitetherapeutics-link">supporting wellness</a> and people in the entertainment industry, while also working in finance and sales.
                </p>
                <p>
                  Learn more about my therapeutic work at{" "}
                  <a href="https://www.elitetherapeutics.ca" target="_blank" rel="noopener noreferrer" className="font-semibold text-forest underline decoration-gold underline-offset-4 hover:text-earth" data-testid="about-elitetherapeutics-line">
                    www.elitetherapeutics.ca
                  </a>.
                </p>
                <p>
                  I've seen the people the world watches. And eventually I began asking myself about the people the world doesn't see.
                </p>
                <p className="border-l-4 border-gold pl-6 font-display text-xl italic leading-relaxed text-forest">
                  What about the women whose stories rarely reach the world's attention? What about children who deserve safety? What about communities living on land whose resources power modern life?
                </p>
                <p>
                  My work hasn't changed as much as it may seem. I'm still caring for people. I'm simply widening the circle. As a holistic practitioner, I've learned that healing is never one-dimensional. It involves the body, the mind, the community and the systems around us.
                </p>
                <p>
                  Congo is part of my history, my heartbeat and my responsibility. I created Nseya Foundation to return to that source — to support people whose lives and communities deserve to be seen, heard and supported.
                </p>
                <p>
                  I was named Nseya — pronounced "say-yah" — after my aunt, who passed tragically in Congo. I never met her, but her story lives through me. Knowing that her life was touched by the realities faced there, I carry this name forward with purpose.
                </p>
                <p className="font-display text-2xl italic text-forest">
                  To turn memory into action. To build community. To restore dignity. That is my promise.
                </p>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-ochre">— Nseya Ariane Kamba</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-sand py-24 lg:py-28" data-testid="philosophy-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            center
            eyebrow="Our Philosophy"
            title="Mutual wellbeing — not charity based on pity."
            sub="Nseya believes that creating change does not have to mean separating ourselves from the people or planet we want to help. When we help ourselves, we can help others. When we care for others, we strengthen our communities. When we care for our communities, we care for the Earth we all share."
          />
          <Reveal delay={0.1}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-3" data-testid="philosophy-values">
              {["Safety", "Dignity", "Clean Water", "Community", "Healing", "Education", "Sustainability"].map((v) => (
                <span key={v} className="rounded-full border border-forest/25 bg-white px-5 py-2.5 font-display text-sm italic text-forest">{v}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* BOARD */}
      <section className="bg-ivory py-24 lg:py-32" data-testid="board-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            eyebrow="Our Board"
            title="Guided by lived experience and purpose."
            sub="The Nseya Foundation board brings together diaspora leadership, humanitarian commitment and professional expertise."
          />
          <div className="mt-14 grid gap-8 md:grid-cols-2" data-testid="board-grid">
            {BOARD.map((member, i) => (
              <Reveal key={member.name} delay={i * 0.1}>
                <article className="h-full rounded-2xl border border-hairline bg-white p-9 shadow-sm" data-testid={`board-member-${i + 1}`}>
                  <div className="flex items-center gap-5">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-forest font-display text-xl italic text-gold" aria-hidden="true">
                      {member.name.split(" ").map((p) => p[0]).join("")}
                    </span>
                    <div>
                      <h3 className="font-display text-2xl font-semibold text-forest">{member.name}</h3>
                      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ochre">{member.role}</p>
                    </div>
                  </div>
                  <p className="mt-6 leading-relaxed text-mutedink">{member.bio}</p>
                  <p className="mt-4 leading-relaxed text-mutedink">{member.bio2}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-14 rounded-2xl border border-hairline bg-sand p-8" data-testid="transparency-note">
              <p className="eyebrow mb-3 text-ochre">Transparency</p>
              <p className="max-w-3xl leading-relaxed text-mutedink">
                We clearly distinguish between Nseya's <strong className="text-ink">current work</strong> — water, community support, wellness, awareness and culture — and our <strong className="text-ink">future vision</strong>: the Women &amp; Children's Sanctuary, a long-term conceptual master plan. Specific project budgets, timelines and fundraising targets will be published as projects are developed and verified.
              </p>
            </div>
          </Reveal>

          <Reveal className="mt-14 text-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-4">
              <DonateButton testid="about-donate-button" />
              <Link to="/get-involved" data-testid="about-get-involved-cta" className="inline-flex items-center gap-2 rounded-full border border-forest/40 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.18em] text-forest transition-all duration-300 hover:bg-forest hover:text-ivory">
                Get Involved <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
